'use client'

import { useState, useEffect } from 'react'
import { Card } from '@/components/ui/card'
import { Loader2, TrendingUp, RefreshCw, AlertTriangle, Wifi, Monitor, Server, Smartphone } from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, Legend,
} from 'recharts'
import apiClient from '@/lib/noc-api-client'
import { useLanguage } from '@/contexts/LanguageContext'

interface ClientEntry {
  id: string
  name: string
  mac: string
  ip: string
  os: string
  connection: string
  ssid: string
  network_name: string
  ap_name: string
  sent_kb: number
  recv_kb: number
  total_kb: number
  anomaly_factor: number
  is_anomaly: boolean
}

interface ApiResponse {
  clients: ClientEntry[]
  total_clients: number
}

function fmtBytes(kb: number): string {
  if (kb >= 1_073_741_824) return `${(kb / 1_073_741_824).toFixed(1)} TB`
  if (kb >= 1_048_576)     return `${(kb / 1_048_576).toFixed(1)} GB`
  if (kb >= 1_024)         return `${(kb / 1_024).toFixed(1)} MB`
  return `${kb.toFixed(0)} KB`
}

function shortName(name: string, max = 20): string {
  return name.length > max ? name.slice(0, max - 1) + '…' : name
}

interface TooltipProps {
  active?: boolean
  payload?: Array<{ payload: ClientEntry & { label: string }; dataKey: string; value: number; color: string }>
}

function CustomTooltip({ active, payload }: TooltipProps) {
  const { t } = useLanguage()
  const ct = t.clientTopTalkers
  if (!active || !payload?.length) return null
  const d = payload[0].payload
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-[8px] shadow-md px-3 py-2 text-[12px] min-w-[200px]">
      <p className="font-medium text-[#101828] mb-0.5 truncate">{d.name}</p>
      {(d.ip || d.mac) && (
        <p className="text-[10px] text-[#939393] mb-1.5 font-mono">
          {d.ip && <span>{d.ip}</span>}
          {d.ip && d.mac && <span className="mx-1">·</span>}
          {d.mac && <span>{d.mac}</span>}
        </p>
      )}
      <div className="space-y-0.5 text-[#4A5565]">
        <div className="flex justify-between gap-4">
          <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-[#535EA4]" />{ct.sent}</span>
          <span className="font-medium text-[#101828]">{fmtBytes(d.sent_kb)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-[#2B7FFF]" />{ct.received}</span>
          <span className="font-medium text-[#101828]">{fmtBytes(d.recv_kb)}</span>
        </div>
        <div className="border-t border-[#F0F0F0] mt-1 pt-1 space-y-0.5">
          {d.os && <div className="flex justify-between"><span>{ct.os}</span><span className="font-medium text-[#101828]">{d.os}</span></div>}
          {d.ssid && <div className="flex justify-between"><span>{ct.ssid}</span><span className="font-medium text-[#101828] max-w-[110px] truncate">{d.ssid}</span></div>}
          <div className="flex justify-between"><span>{ct.network}</span><span className="font-medium text-[#101828] max-w-[110px] truncate">{d.network_name}</span></div>
        </div>
        {d.is_anomaly && (
          <div className="flex items-center gap-1 mt-1 text-[#F59E0B]">
            <AlertTriangle className="h-3 w-3 flex-shrink-0" />
            <span>{d.anomaly_factor}{ct.aboveAverage}</span>
          </div>
        )}
      </div>
    </div>
  )
}

export function ClientTopTalkersWidget() {
  const { t } = useLanguage()
  const ct = t.clientTopTalkers
  const [data, setData] = useState<ApiResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError]   = useState<string | null>(null)

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      const res: ApiResponse = await apiClient('/api/dashboard/client-top-talkers?limit=10')
      setData(res)
    } catch (err) {
      console.error('ClientTopTalkers fetch error:', err)
      setError('Failed to load client data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    // Auto-refresh every 60 seconds (live data)
    const interval = setInterval(() => { fetchData() }, 60000)
    return () => clearInterval(interval)
  }, [])

  const clients = (data?.clients ?? []).map(c => ({ ...c, label: shortName(c.name) }))
  const anomalyCount = clients.filter(c => c.is_anomaly).length

  // OS type breakdown for legend badge
  const osTypes = [...new Set(clients.map(c => c.os).filter(Boolean))]

  return (
    <Card className="bg-white border border-[#E5E7EB] shadow-none rounded-[10px] p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-[#4A5565]" />
          <span className="text-[15px] font-medium text-[#101828] tracking-[-0.2px]">
            {ct.title}
          </span>
          <span className="text-[11px] text-[#939393] font-normal ml-1">
            {ct.subtitle}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {anomalyCount > 0 && (
            <span className="flex items-center gap-1 text-[10px] text-[#F59E0B] font-medium bg-[#FEF3C7] rounded-full px-2 py-0.5">
              <AlertTriangle className="h-3 w-3" />{anomalyCount} spike{anomalyCount !== 1 ? 's' : ''}
            </span>
          )}
          <button onClick={fetchData} className="p-1.5 rounded-[6px] hover:bg-[#F5F5F5] transition-colors" title="Refresh">
            <RefreshCw className="h-3.5 w-3.5 text-[#939393]" />
          </button>
        </div>
      </div>

      {/* OS badges */}
      {!loading && !error && clients.length > 0 && (
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {osTypes.slice(0, 4).map(os => {
            const osLower = os.toLowerCase()
            const icon = osLower.includes('windows') || osLower.includes('macos') || osLower.includes('linux')
              ? <Monitor className="h-2.5 w-2.5" />
              : osLower.includes('iphone') || osLower.includes('android') || osLower.includes('ios')
              ? <Smartphone className="h-2.5 w-2.5" />
              : osLower.includes('nec') || osLower.includes('h3c') || osLower.includes('cisco') || osLower.includes('juniper')
              ? <Server className="h-2.5 w-2.5" />
              : <Wifi className="h-2.5 w-2.5" />
            return (
              <span key={os} className="flex items-center gap-1 text-[10px] text-[#676767] bg-[#F5F5F5] rounded-full px-2 py-0.5">
                {icon}
                {os}
              </span>
            )
          })}
          {data?.total_clients && data.total_clients > 10 && (
            <span className="text-[10px] text-[#939393]">{data.total_clients} {ct.totalClients}</span>
          )}
        </div>
      )}

      {/* Chart */}
      {loading ? (
        <div className="h-[220px] flex items-center justify-center gap-2 text-[13px] text-[#939393]">
          <Loader2 className="h-4 w-4 animate-spin" /> {ct.loading}
        </div>
      ) : error ? (
        <div className="h-[220px] flex items-center justify-center text-[13px] text-[#CD585A]">{error}</div>
      ) : clients.length === 0 ? (
        <div className="h-[220px] flex flex-col items-center justify-center gap-1 text-center">
          <p className="text-[13px] text-[#4A5565]">{ct.noData}</p>
          <p className="text-[11px] text-[#939393]">{ct.noDataSub}</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={clients} layout="vertical" margin={{ top: 2, right: 12, left: 4, bottom: 2 }} barCategoryGap="18%" barGap={1}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" horizontal={false} />
            <XAxis
              type="number"
              tick={{ fontSize: 10, fill: '#939393' }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v: number) => fmtBytes(v)}
            />
            <YAxis
              type="category"
              dataKey="label"
              tick={{ fontSize: 10, fill: '#939393' }}
              tickLine={false}
              axisLine={false}
              width={100}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: '#F9FAFB' }} />
            <Legend wrapperStyle={{ fontSize: 11, color: '#4A5565', paddingTop: 6 }} />
            <Bar dataKey="sent_kb" name={ct.sent} stackId="a" fill="#535EA4" radius={[0, 0, 0, 0]}>
              {clients.map((c, i) => (
                <Cell key={`s-${i}`} fill={c.is_anomaly ? '#EF4444' : '#535EA4'} opacity={0.9} />
              ))}
            </Bar>
            <Bar dataKey="recv_kb" name={ct.received} stackId="a" fill="#2B7FFF" radius={[0, 3, 3, 0]}>
              {clients.map((c, i) => (
                <Cell key={`r-${i}`} fill={c.is_anomaly ? '#F87171' : '#2B7FFF'} opacity={0.75} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  )
}
